import { useState, useRef, useEffect } from "react";
import { api } from "../../App";
import { Shield, Lock, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ee59bb71-25b5-4f3e-ade6-7b45b4f149b6/artifacts/v205dw0b_IMG_7633.png";

export function PinGate({ user, onVerified }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const inputRefs = useRef([]);

  // Auto-focus first input on mount
  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError(null);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled (4-6 digits)
    const pin = next.join("");
    if (pin.length >= 4 && next.slice(0, pin.length).every(d => d)) {
      // Only auto-submit at 4, 5, or 6 digits if the next slot is empty or we're at 6
      const filledCount = next.filter(d => d).length;
      if (filledCount === 6 || (filledCount >= 4 && index === filledCount - 1 && !next[filledCount])) {
        // Wait a beat for the UI to update, then submit
        setTimeout(() => handleSubmit(pin.slice(0, filledCount)), 150);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
    }
    if (e.key === "Enter") {
      const pin = digits.join("");
      if (pin.length >= 4) handleSubmit(pin);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length >= 4) {
      const next = ["", "", "", "", "", ""];
      for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
      setDigits(next);
      setTimeout(() => handleSubmit(pasted), 150);
    }
  };

  const handleSubmit = async (pin) => {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    try {
      await api.post("/auth/pin/verify", { pin });
      toast.success("PIN verified");
      onVerified();
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);

      if (newAttempts >= 5) {
        setError("Too many failed attempts. Please sign out and try again.");
      } else {
        setError(`Incorrect PIN. ${5 - newAttempts} attempt${5 - newAttempts !== 1 ? "s" : ""} remaining.`);
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="pin-gate">
      <div className="w-full max-w-sm">
        {/* Logo & branding */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="w-14 h-14 mx-auto mb-3 object-contain" />
          <h1 className="font-heading text-xl font-bold text-slate-800">Verify Your Identity</h1>
          <p className="text-sm text-slate-500 mt-1">
            Enter your session PIN to continue
          </p>
        </div>

        {/* PIN Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
          {/* User info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <span className="text-forest font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-700 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <Lock className="w-4 h-4 text-forest/50 flex-shrink-0" />
          </div>

          {/* PIN input boxes */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-3 block text-center">
              Session PIN (4-6 digits)
            </label>
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  disabled={verifying || attempts >= 5}
                  className={`
                    w-11 h-13 text-center text-xl font-mono font-bold rounded-xl border-2 
                    outline-none transition-all duration-150
                    ${error
                      ? "border-red-300 bg-red-50/50"
                      : digit
                        ? "border-forest bg-forest/5 text-forest"
                        : "border-slate-200 bg-white text-slate-800"
                    }
                    focus:border-forest focus:ring-2 focus:ring-forest/20
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  data-testid={`pin-gate-digit-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100" data-testid="pin-gate-error">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Loading state */}
          {verifying && (
            <div className="flex items-center justify-center gap-2 text-forest">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Verifying...</span>
            </div>
          )}

          {/* Manual submit button (if auto-submit didn't trigger) */}
          <Button
            onClick={() => handleSubmit(digits.join(""))}
            disabled={verifying || digits.join("").length < 4 || attempts >= 5}
            className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl h-11"
            data-testid="pin-gate-submit"
          >
            {verifying ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Shield className="w-4 h-4 mr-2" />
            )}
            Verify PIN
          </Button>
        </div>

        {/* HIPAA badge */}
        <div className="flex items-center justify-center gap-2 mt-6 text-slate-400">
          <Shield className="w-3.5 h-3.5" />
          <span className="text-xs">HIPAA Multi-Factor Authentication</span>
        </div>
      </div>
    </div>
  );
}
